import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TimelineCard = ({ experience, index, isExpanded, onToggle }) => {
  const isEven = index % 2 === 0;

  return (
    <div className={`relative flex items-start mb-8 lg:mb-12 lg:items-center ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
      {/* Timeline Line - Enhanced visibility */}
      <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-purple-600 via-blue-500 to-purple-600 h-full opacity-70 shadow-lg"></div>
      
      {/* Timeline Dot */}
      <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-5 h-5 bg-gradient-to-br from-purple-600 to-blue-500 rounded-full border-4 border-white shadow-xl z-10 ring-2 ring-purple-200"></div>

      {/* Content Card */}
      <div className={`w-full lg:w-5/12 pl-8 lg:pl-0 ${isEven ? 'lg:pr-8' : 'lg:pl-8'}`}>
        <div className="bg-white rounded-xl shadow-premium border border-border p-4 sm:p-6 hover-lift transition-premium">
          {/* Company Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4">
            <div className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
              <Image 
                src={experience.companyLogo} 
                alt={`${experience.company} logo`}
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-text-primary truncate">{experience.position}</h3>
              <p className="text-xs sm:text-sm text-text-secondary truncate">{experience.company}</p>
            </div>
            <div className="text-left sm:text-right w-full sm:w-auto flex-shrink-0">
              <p className="text-xs sm:text-sm font-medium text-primary">{experience.duration}</p>
              <p className="text-xs text-text-muted">{experience.type}</p>
            </div>
          </div>

          {/* Quick Summary */}
          <p className="text-text-secondary mb-4 leading-relaxed">{experience.summary}</p>

          {/* Key Technologies */}
          <div className="flex flex-wrap gap-2 mb-4">
            {experience.technologies.slice(0, 4).map((tech, idx) => (
              <span 
                key={idx}
                className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
              >
                {tech}
              </span>
            ))}
            {experience.technologies.length > 4 && (
              <span className="px-3 py-1 bg-surface text-text-muted text-xs font-medium rounded-full">
                +{experience.technologies.length - 4} more
              </span>
            )}
          </div>

          {/* Expand/Collapse Button */}
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
            onClick={() => onToggle(experience.id)}
            className="w-full justify-between"
          >
            {isExpanded ? 'Show Less' : 'View Details'}
          </Button>

          {/* Expanded Content */}
          {isExpanded && (
            <div className="mt-6 pt-6 border-t border-border space-y-4 sm:space-y-6">
              {/* Key Achievements */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-3 flex items-center">
                  <Icon name="Trophy" size={16} className="mr-2 text-warning flex-shrink-0" />
                  Key Achievements
                </h4>
                <ul className="space-y-2">
                  {experience.achievements.map((achievement, idx) => (
                    <li key={idx} className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={14} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-text-secondary leading-relaxed">{achievement}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Major Projects */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-3 flex items-center">
                  <Icon name="Code" size={16} className="mr-2 text-accent flex-shrink-0" />
                  Major Projects
                </h4>
                <div className="space-y-3">
                  {experience.projects.map((project, idx) => (
                    <div key={idx} className="bg-surface/50 rounded-lg p-3 sm:p-4">
                      <h5 className="text-sm sm:text-base font-medium text-text-primary mb-1">{project.name}</h5>
                      <p className="text-xs sm:text-sm text-text-secondary mb-2 leading-relaxed">{project.description}</p>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex flex-wrap gap-1">
                          {project.technologies.map((tech, techIdx) => (
                            <span 
                              key={techIdx}
                              className="px-2 py-0.5 bg-white text-text-muted text-xs rounded"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-success font-medium whitespace-nowrap">{project.impact}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team & Collaboration */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-3 flex items-center">
                  <Icon name="Users" size={16} className="mr-2 text-brand-purple flex-shrink-0" />
                  Team & Collaboration
                </h4>
                <div className="bg-surface/50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-text-secondary mb-2 leading-relaxed">{experience.teamInfo}</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-text-muted">
                    <span className="flex items-center">
                      <Icon name="Users" size={12} className="mr-1 flex-shrink-0" />
                      Team Size: {experience.teamSize}
                    </span>
                    <span className="flex items-center">
                      <Icon name="MapPin" size={12} className="mr-1 flex-shrink-0" />
                      {experience.location}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recommendation */}
              {experience.recommendation && (
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-text-primary mb-3 flex items-center">
                    <Icon name="Quote" size={16} className="mr-2 text-secondary flex-shrink-0" />
                    Recommendation
                  </h4>
                  <div className="bg-surface/50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-text-secondary italic mb-3 leading-relaxed">"{experience.recommendation.quote}"</p>
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="User" size={14} className="text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-text-primary truncate">{experience.recommendation.author}</p>
                        <p className="text-xs text-text-muted truncate">{experience.recommendation.position}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Timeline Dot */}
      <div className="lg:hidden absolute left-2 top-6 w-3 h-3 bg-gradient-to-br from-purple-600 to-blue-500 rounded-full border-2 border-white shadow-lg z-10"></div>
    </div>
  );
};

export default TimelineCard;